<template>
    <template v-if="!isLoading && page">
        <div class="min-h-dvh tw-relative tw-flex tw-flex-col tw-justify-center tw-pt-[1px] tw-text-center">
            <div class="left-side"></div>
            <div class="right-side"></div>
            <div class="io-container tw-z-10 tw-mt-0">
                <h1
                    class="tw-mb-4 tw-text-2xl lg:tw-text-4xl"
                    v-html="page.page.title"></h1>
                <h3
                    class="tw-mb-8 tw-text-base tw-font-medium lg:tw-text-lg"
                    v-html="page.page.subTitle"></h3>
            </div>
            <NewsSliderNews
                v-if="page.lastNews?.length"
                class="tw-z-10 tw-px-4 lg:tw-px-10"
                :news="page.lastNews" />
        </div>
        <div
            v-if="page.nextEvents?.length"
            class="io-container">
            <h1
                class="tw-mb-10 tw-text-2xl lg:tw-text-4xl"
                v-html="page.page.nextEvents"></h1>
            <EventsSlider :events="page.nextEvents" />
        </div>
        <div class="io-container">
            <NewsletterSmall />
        </div>
        <div
            v-if="page.tags?.[0] || page.tags?.[1]"
            class="io-container tw-grid tw-grid-cols-1 tw-gap-16 lg:tw-grid-cols-2">
            <LastNewsOf
                v-if="page.tags?.[0]"
                layout="col"
                :tag="page.tags?.[0]" />
            <LastNewsOf
                v-if="page.tags?.[1]"
                layout="col"
                :tag="page.tags?.[1]" />
        </div>
        <div
            v-if="page.products?.length"
            class="io-container">
            <ProductsGrid :products="page.products" />
        </div>
        <div
            v-if="page.tags?.[2]"
            class="io-container">
            <LastNewsOf
                v-if="page.tags?.[2]"
                layout="row"
                :tag="page.tags?.[2]" />
        </div>
        <NewsBanner
            v-if="page.banners?.[0]"
            :banner="page.banners?.[0]" />
        <div
            v-if="page.tags?.[3]"
            class="io-container">
            <LastNewsOf
                v-if="page.tags?.[3]"
                layout="row"
                :tag="page.tags?.[3]" />
        </div>
        <NewsBanner
            v-if="page.banners?.[1]"
            :banner="page.banners?.[1]" />
        <div
            v-if="moreTags.length"
            class="io-container">
            <h2
                class="tw-mb-10 tw-text-center tw-text-2xl lg:tw-text-4xl"
                v-html="page.page.moreTagsTitle"></h2>
            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 lg:tw-gap-8 xl:tw-h-[600px] xl:tw-grid-cols-3 xl:tw-grid-rows-12">
                <router-link
                    v-for="(tag, index) in moreTags"
                    :key="tag.id"
                    class="tw-relative tw-col-span-1 tw-rounded-3xl tw-bg-cover tw-bg-center tw-bg-no-repeat max-xl:tw-aspect-[3/1]"
                    :class="{ 'xl:tw-row-span-6': index !== 2, 'xl:tw-row-span-9': index === 2 }"
                    :style="{
                        backgroundImage: `url(${useMedia(tag.image).medium})`,
                    }"
                    :to="{ name: 'AllNews', query: { tag: tag.slug } }">
                    <span class="tw-absolute tw-bottom-4 tw-left-0 tw-right-0 tw-p-4 tw-text-center tw-text-2xl lg:tw-text-3xl tw-font-semibold tw-text-white">{{ tag.name }}</span>
                </router-link>
                <div
                    class="tw-relative tw-col-span-1 tw-rounded-3xl tw-bg-cover tw-bg-center tw-bg-no-repeat max-xl:tw-aspect-[3/1] xl:tw-row-span-3"
                    :style="{
                        backgroundImage: `url(${moreTagsAllArticlesImage || ''})`,
                    }">
                    <div class="tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-[50%] -tw-translate-y-[50%] tw-transform tw-text-center">
                        <v-btn
                            class="tw-h-12 tw-px-6"
                            color="primary"
                            elevation="0"
                            flat
                            rounded="pill"
                            :to="{ name: 'AllNews' }">
                            <span class="tw-text-base tw-font-light tw-text-white">{{ $t('news.allArticles') }}</span>
                        </v-btn>
                    </div>
                </div>
            </div>
        </div>
        <NewsletterFooter />
    </template>
    <template v-else>
        <!-- loader -->
        <Loader />
    </template>
</template>

<script setup lang="ts">
    import { getNewsPage } from '@/api/news';
    import Loader from '@/components/Loader.vue';
    import { useAppStore } from '@/store/app';
    import { useMedia } from '@/composables/useMedia';
    import { useI18n } from 'vue-i18n';

    const { t } = useI18n();
    const appStore = useAppStore();

    const page = ref<any>(null);
    const isLoading = ref(false);

    const moreTags = computed(() => {
        return page.value?.moreTags || [];
    });

    const moreTagsAllArticlesImage = computed(() => {
        if (!page.value?.page?.moreTagsAllArticlesImage) return null;
        return useMedia(page.value?.page?.moreTagsAllArticlesImage).medium;
    });

    async function init() {
        isLoading.value = true;
        try {
            const { data } = await getNewsPage();
            page.value = data;
            console.log('page', page.value);

            isLoading.value = false;
        } catch (error) {
            console.error('Failed to load news page', error);
        }
    }

    init();

    document.title = t('news.docTitle');
</script>

<style scoped>
    .left-side {
        position: absolute;
        left: 0;
        top: 0;
        width: 50%;
        height: 100%;
        /* UI Properties */
        background: transparent linear-gradient(90deg, rgb(var(--v-theme-primary)) 0%, #80808000 100%) 0% 0% no-repeat padding-box;
        opacity: 0.25;
    }

    .right-side {
        position: absolute;
        right: 0;
        top: 0;
        width: 50%;
        height: 100%;
        /* UI Properties */
        background: transparent linear-gradient(270deg, rgb(var(--v-theme-primary)) 0%, #80808000 100%) 0% 0% no-repeat padding-box;
        opacity: 0.25;
    }
</style>
